<script lang="tsx" setup>
import { Avatar } from '#components'
import { animateDOM, useTeleport } from 'core'
import { getData, useAudioPlayer, useComments } from './logic'

const styles = useCssModule()
const metaNode = ref()
const showDockedPlayer = ref(false)
const [playerRef, DockedPlayer] = useTeleport()

const data = getData()
const { Player, getAudio } = useAudioPlayer()
const comments = useComments()

function Profile() {
  return <Avatar name={data.user.name} src={data.user.avatarUrl} class={styles.logo} size="120" draggable="false" />
}

function showCommentDialog() {
  comments.showDialog(getAudio().currentTime)
}

if (import.meta.client) {
  useIntersectionObserver(metaNode, ([props]) => {
    const isHidden = !props.isIntersecting

    // Exit if nothing changed
    if (showDockedPlayer.value === isHidden)
      return

    animateDOM(() => showDockedPlayer.value = isHidden)
  })
}
</script>

<template>
  <div :class="[$style.view, comments.enabled && comments.list.length && $style.has_comments]" :data-bottom-player="showDockedPlayer">
    <Profile class="mr-6" />

    <div ref="metaNode" :class="$style.audio_player_meta">
      <svg :class="$style.arrow_sign" width="36" height="40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="m6 26-6-4v-4l6-4L25 3l7-3 3 2 1 7v22l-1 7-3 2-7-3L6 26Z" fill="currentColor" />
      </svg>

      <div v-once class="flex gap-4">
        <Profile class="mr-6" />

        <h4 class="font-semibold text-base sm:text-lg lg:text-heading-4">
          <span class="text-$color-primary">{{ data.settings.name }}{{ ' ' }}</span>
          <span class="text-$color-neutral opacity-70 font-normal">{{ data.settings.strings.sharedWithYou }}</span>
        </h4>
      </div>

      <div class="h-40px">
        <Player ref="playerRef" class="w-full text-$color-neutral" :class="showDockedPlayer && $style.docked_audio_player">
          <div v-if="comments.enabled && showDockedPlayer" class="ml-2 before:content-[''] before:absolute before:top-0 before:h-full before:w-1px before:bg-$color-neutral-border">
            <CTAButton class="ml-4 $border-color-[$color-neutral-border] $focus-outline-color-[$color-neutral-border]" variant="ghost" size="sm" @click="showCommentDialog">
              <Icon name="i-solar-chat-round-line-linear" />
              <span class="<xs:hidden">Comment</span>
            </CTAButton>
          </div>
        </Player>
      </div>
    </div>

    <!-- Transcription -->
    <PageListenViewCaptions :class="$style.transcript" />
  </div>

  <!-- Docked Position -->
  <DockedPlayer v-if="showDockedPlayer" class="w-full sticky bottom-5 flex-center" />
</template>

<style module lang="sass">
.view
  @apply mx-auto gap-6 w-full max-w-800px grid
  @apply animate-name-fade animate-duration-500

  grid-template: "logo main" max-content "logo transcript" / max-content 1fr

  @screen xl
    &:not([data-bottom-player=true])
      transform: translateX(-60px)

    // Align profile logo with header
    // Allows space for comments
    &.has_comments:has(.transcript[has-captions=true])
      @apply max-w-752px
      transform: translateX(-136px) !important

  @screen lt-sm
    grid-template: "main" max-content "transcript"

    & > .logo
      @apply hidden

.logo
  grid-area: logo

.transcript
  @apply view-transition-transcript
  grid-area: transcript

.audio_player_meta
  @apply mb-10.5 p-6 gap-6 grid relative
  @apply rounded-2.5 bg-$color-background-elevated border-1 border-solid border-$color-neutral-border

  box-shadow: 0px 2px 16px 8px var(--color-elevation)
  grid-area: main

  .arrow_sign
    @apply lt-sm:hidden w-40px h-36px absolute top-24px left-24px text-$color-background-elevated stroke-$color-neutral-border -z-1 translate-x-[-100%]

  .logo
    @apply h-47px w-47px sm:hidden

.docked_audio_player
  @apply w-full max-w-800px py-6 px-4 rounded-2.5 bg-$color-primary-5 border-1 border-solid border-$color-neutral-border backdrop-blur-12px

  @apply animate-name-fade-scale-up animate-duration-500

// ! Scrolling view
.view[data-bottom-player=true]
  @screen sm
    grid-template: "main" max-content "transcript"

    .logo
      @apply hidden
</style>
